/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Shared content-block renderer. Reads `ArticleContentBlock` data and renders
 * each kind (rich, faq, table, callout, quote, divider, image). Lives outside
 * `pages/ArticlePage.tsx` so site pages can render freeform blocks with the
 * exact same visual treatment as kennisbank articles. The block data model
 * is the article model — see `src/pages/admin/articleTypes.ts`.
 */

import React, { useEffect, useRef, useState } from 'react';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  Lightbulb,
  Quote as QuoteIcon,
} from 'lucide-react';
import type {
  ArticleContentBlock,
  ArticleCTA,
  CalloutContentBlock,
  DividerContentBlock,
  FAQContentBlock,
  ImageContentBlock,
  QuoteContentBlock,
  RichContentBlock,
  TableContentBlock,
} from '../pages/admin/articleTypes';
import {
  blockHeadingId,
  getSectionHeading,
  isCalloutBlock,
  isDividerBlock,
  isFAQBlock,
  isImageBlock,
  isQuoteBlock,
  isTableBlock,
  resolveTableStyle,
} from '../pages/admin/articleTypes';
import { currentPageSurface, splitLanguageFromPath, trackEvent } from '../lib/gtag';
import { useLanguage } from '../contexts/LanguageContext';
import { withComparerStart } from '../lib/compareAnchor';

const BlockHeading: React.FC<{ block: ArticleContentBlock }> = ({ block }) => {
  // Hook before the conditional return — hooks must be unconditional.
  const { t } = useLanguage();
  const heading = getSectionHeading(block);
  if (!heading) return null;
  const id = blockHeadingId(block);
  return (
    <h2 id={id} className="group text-2xl md:text-3xl font-black text-white mb-5 leading-tight">
      {heading}
      {/* Hover-revealed copyable deep link. A literal "#" (no icon) keeps the
          SSR markup byte-identical to the client render; native anchor
          behavior + the root's scroll-padding-top make it work with zero JS
          in baked HTML.
          aria-hidden + tabIndex=-1 on purpose: it's a visual affordance only —
          including it in the a11y tree would pollute every heading's
          accessible name, and keyboard/SR users already navigate via the TOC
          (which also sets the URL hash). */}
      <a
        href={`#${id}`}
        aria-hidden="true"
        tabIndex={-1}
        title={t('kennisbank.heading_anchor_label')}
        className="ml-2 inline-block align-baseline no-underline text-emerald-500 hover:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        #
      </a>
    </h2>
  );
};

export const PROSE_BASE =
  'prose prose-invert prose-emerald max-w-none text-slate-300 leading-relaxed ' +
  'prose-headings:text-white prose-headings:font-black ' +
  'prose-a:text-emerald-400 prose-a:underline prose-a:underline-offset-4 prose-a:decoration-emerald-500/30 hover:prose-a:text-emerald-300 hover:prose-a:decoration-emerald-400/60 ' +
  'prose-strong:text-white ' +
  'prose-code:text-emerald-300 prose-code:bg-slate-800/60 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:font-bold prose-code:before:content-none prose-code:after:content-none ' +
  'prose-pre:bg-slate-900 prose-pre:border prose-pre:border-slate-800 prose-pre:rounded-2xl ' +
  'prose-hr:border-slate-800';

export const CTABanner: React.FC<{ cta: ArticleCTA }> = ({ cta }) => (
  <div className="my-10 bg-emerald-900/20 border border-emerald-800/50 rounded-3xl p-8 reveal-rise">
    <h3 className="text-xl font-black text-emerald-300 mb-2">{cta.title}</h3>
    <p className="text-emerald-400 text-sm leading-relaxed font-medium mb-6">{cta.text}</p>
    <a
      href={withComparerStart(cta.buttonUrl)}
      className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white font-black rounded-2xl transition-all shadow-lg shadow-emerald-900/20 group"
    >
      {cta.buttonText}
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </a>
  </div>
);

/**
 * A div of sanitized CMS HTML that keeps its DOM across re-renders.
 *
 * React 19 assigns `innerHTML` again whenever the `dangerouslySetInnerHTML`
 * prop is a new object, which an inline `{{ __html }}` is on every render,
 * even when the string has not changed. Every node inside is replaced: a link
 * that had keyboard focus drops it to <body> and a text selection is lost. An
 * article re-renders each time the scroll-spy passes a heading, so a Tab that
 * scrolled the page threw the visitor's focus away (measured 2026-10-02).
 * Memoised on the string, a re-render with the same text touches nothing; the
 * markup is exactly what the inline form rendered, so the bake is unchanged.
 */
const SanitizedHtml = React.memo(function SanitizedHtml({
  html,
  className,
}: {
  html: string;
  className?: string;
}) {
  return <div className={className} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />;
});

const RichBlock: React.FC<{ block: RichContentBlock }> = ({ block }) => {
  const hasImage = Boolean(block.image);
  // Back-compat: legacy blocks only carry imagePosition; new blocks use imageAlignment.
  const alignment = block.imageAlignment ?? block.imagePosition ?? 'left';

  let imageClass = 'w-full rounded-2xl object-cover mb-6';
  if (alignment === 'left') {
    imageClass = `w-full md:w-2/5 rounded-2xl object-cover mb-6 md:float-left md:mr-8 md:mb-4 md:mt-2`;
  } else if (alignment === 'right') {
    imageClass = `w-full md:w-2/5 rounded-2xl object-cover mb-6 md:float-right md:ml-8 md:mb-4 md:mt-2`;
  } else if (alignment === 'center') {
    imageClass = 'mx-auto block w-full md:w-3/4 rounded-2xl object-cover mb-6';
  } else if (alignment === 'full') {
    imageClass = 'block w-full rounded-2xl object-cover mb-6';
  }

  const altText = block.imageAlt ?? block.heading ?? '';

  return (
    <>
      <BlockHeading block={block} />
      {hasImage ? (
        <div
          className={`${PROSE_BASE} prose-img:my-0 after:content-[''] after:block after:clear-both`}
        >
          <img src={block.image} alt={altText} className={imageClass} />
          <SanitizedHtml html={block.text} />
        </div>
      ) : (
        <SanitizedHtml className={PROSE_BASE} html={block.text} />
      )}
      {block.cta && <CTABanner cta={block.cta} />}
    </>
  );
};

const FAQBlockRender: React.FC<{ block: FAQContentBlock }> = ({ block }) => (
  <>
    <BlockHeading block={block} />
    <div className="space-y-3">
      {block.items.map((item, index) => (
        <details
          key={item.id}
          className="group bg-slate-900/40 border border-slate-800 rounded-2xl px-6 py-4 open:bg-slate-900/60"
          onToggle={(e) => {
            const { language } = splitLanguageFromPath(window.location.pathname);
            trackEvent(e.currentTarget.open ? 'faq_expand' : 'faq_collapse', {
              faq_index: index,
              faq_question: item.q?.slice(0, 80),
              surface: currentPageSurface(),
              language,
            });
          }}
        >
          <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-white font-black">
            <span>{item.q}</span>
            <ChevronDown className="w-4 h-4 text-fg-muted transition-transform group-open:rotate-180 shrink-0" />
          </summary>
          <SanitizedHtml
            className="prose prose-invert prose-emerald max-w-none mt-4 text-slate-300
              prose-headings:text-white prose-strong:text-white
              prose-a:text-emerald-400 prose-a:underline prose-a:underline-offset-4 prose-a:decoration-emerald-500/30 hover:prose-a:text-emerald-300"
            html={item.a || ''}
          />
        </details>
      ))}
    </div>
  </>
);

const TABLE_DENSITY_CELL: Record<string, string> = {
  compact: 'px-3 py-2 text-xs',
  cozy: 'px-4 py-3 text-sm',
  comfortable: 'px-6 py-4 text-base',
};

const TABLE_ALIGN: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const TABLE_CELL_BORDER: Record<string, string> = {
  none: '',
  horizontal: '',
  all: 'border-r border-slate-800/70 last:border-r-0',
};

// Minimum readable column width for tables with 3+ columns. Without a floor,
// `w-full` auto layout crushes phone columns to ~110px and cells wrap to 4-5
// lines — while the table still overflows the article column with nothing
// signalling that the last column is cut off. With the floor, narrow viewports
// scroll a readable table instead. 1-2 column tables always fit the column.
const TABLE_MIN_COL_REM = 9;
const TABLE_EDGE_FADE = '2.5rem';

const TableBlockRender: React.FC<{ block: TableContentBlock }> = ({ block }) => {
  const rows = Array.isArray(block.rows) ? block.rows : [];
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  // null = unmeasured (SSR + first client paint). The unmeasured render keeps
  // the scroll-safe overflow-x-auto default so prerendered HTML degrades to a
  // plain scrollable table before hydration.
  const [canScroll, setCanScroll] = useState<boolean | null>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const measure = () => {
      setCanScroll(scroller.scrollWidth > scroller.clientWidth + 1);
      setAtStart(scroller.scrollLeft <= 1);
      setAtEnd(scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1);
    };
    measure();
    scroller.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(scroller);
    if (tableRef.current) ro?.observe(tableRef.current);
    return () => {
      scroller.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, []);

  if (rows.length === 0) {
    return <BlockHeading block={block} />;
  }
  const style = resolveTableStyle(block);
  const colCount = rows[0]?.length ?? 0;
  const headerRow = style.hasHeader && style.headerStyle !== 'none' ? rows[0] : null;
  const bodyRows = style.hasHeader ? rows.slice(1) : rows;
  const cellPad = TABLE_DENSITY_CELL[style.density];
  const tdBorder = TABLE_CELL_BORDER[style.borders];
  const rowBorder = style.borders === 'none' ? '' : 'border-b border-slate-800/70';
  const stripeClass = style.striped ? 'even:bg-slate-900/30' : '';
  // Once measured as fitting, drop the scroll container so the header cells
  // can stick to the viewport (any overflow != visible/clip would trap them).
  const isFit = canScroll === false;

  // Edge fades: mask the scroller so cut-off content visibly "continues"
  // past the edge. An alpha mask (not colour overlays) renders correctly over
  // any background in both themes.
  let maskImage: string | undefined;
  if (canScroll) {
    if (atStart && !atEnd) {
      maskImage = `linear-gradient(to right, #000 calc(100% - ${TABLE_EDGE_FADE}), transparent)`;
    } else if (!atStart && atEnd) {
      maskImage = `linear-gradient(to right, transparent, #000 ${TABLE_EDGE_FADE})`;
    } else if (!atStart && !atEnd) {
      maskImage = `linear-gradient(to right, transparent, #000 ${TABLE_EDGE_FADE}, #000 calc(100% - ${TABLE_EDGE_FADE}), transparent)`;
    }
  }

  // Caption lives outside the scroller (a <caption> scrolls and clips with
  // the table) and stays readable at any table width; aria-describedby keeps
  // the association for assistive tech.
  const captionId = block.caption ? `table-caption-${block.id}` : undefined;
  const caption = block.caption ? (
    <p
      id={captionId}
      className={`px-1 text-xs text-fg-muted font-medium ${
        style.captionPosition === 'top' ? 'mt-1 mb-3' : 'mt-3 mb-1'
      }`}
    >
      {block.caption}
    </p>
  ) : null;

  return (
    <div className="mb-12 last:mb-0">
      <BlockHeading block={block} />
      {style.captionPosition === 'top' && caption}
      {/* overflow-clip (not hidden/auto) rounds the corners without creating
          a scroll container, so sticky header cells keep working. */}
      <div className="relative overflow-clip rounded-2xl border border-slate-800">
        <div
          ref={scrollerRef}
          className={`${
            isFit ? '' : 'overflow-x-auto overscroll-x-contain scrollbar-thin '
          }focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500/70`}
          style={maskImage ? { maskImage, WebkitMaskImage: maskImage } : undefined}
          tabIndex={canScroll ? 0 : undefined}
          role={canScroll && block.heading ? 'region' : undefined}
          aria-labelledby={canScroll && block.heading ? blockHeadingId(block) : undefined}
        >
          {/* border-separate: collapsed borders detach from sticky cells in
              Chromium, so all borders live on the cells themselves. */}
          <table
            ref={tableRef}
            className="w-full text-left border-separate border-spacing-0"
            style={colCount >= 3 ? { minWidth: `${colCount * TABLE_MIN_COL_REM}rem` } : undefined}
            aria-describedby={captionId}
          >
            {headerRow && (
              <thead>
                <tr>
                  {headerRow.map((cell, ci) => (
                    <th
                      key={ci}
                      scope="col"
                      data-follows-header={isFit || undefined}
                      className={`${cellPad} ${TABLE_ALIGN[style.columnAlignments[ci] ?? 'left']} ${tdBorder} font-black text-white bg-slate-900 border-b border-slate-700 align-top${
                        /* Sticks at the header's live bottom edge
                           (--header-offset, index.css), not a measured header
                           offsetHeight — the header hides with a transform, so
                           its layout height stays 80px while it is visually
                           gone — and glides along when the header flips
                           (src/lib/headerFollow.ts). A translate by the offset,
                           the previous version, pushed the resting row up into
                           the wrapper's overflow-clip whenever the header was
                           away — on a phone the whole row, on desktop 39 of its
                           45 px. */
                        isFit ? ' sticky top-[var(--header-offset)] z-[1]' : ''
                      }`}
                    >
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {bodyRows.map((row, ri) => {
                const isLastRow = ri === bodyRows.length - 1;
                return (
                  <tr key={ri} className={stripeClass}>
                    {row.map((cell, ci) => {
                      const align = TABLE_ALIGN[style.columnAlignments[ci] ?? 'left'];
                      const isRowHeader = style.headerStyle === 'top-left' && ci === 0;
                      return (
                        <td
                          key={ci}
                          className={`${cellPad} ${align} ${tdBorder} ${
                            isLastRow ? '' : rowBorder
                          } align-top whitespace-pre-line ${
                            isRowHeader
                              ? 'font-black text-white bg-slate-900/40'
                              : 'text-slate-300 font-medium'
                          }`}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* One-time swipe affordance: the hidden column rarely peeks (auto
            layout parks it fully off-screen), so an explicit hint carries the
            "this scrolls" signal. Disappears on first scroll. */}
        {canScroll && atStart && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-2 flex items-center"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-800/90 border border-slate-700 shadow-lg motion-safe:animate-pulse">
              <ChevronRight className="w-4 h-4 text-slate-200" />
            </span>
          </div>
        )}
      </div>
      {style.captionPosition === 'bottom' && caption}
    </div>
  );
};

const CALLOUT_VARIANTS = {
  info: {
    Icon: Info,
    bg: 'bg-sky-500/10',
    border: 'border-sky-500/30',
    iconColor: 'text-sky-300',
    heading: 'text-sky-100',
  },
  warning: {
    Icon: AlertTriangle,
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    iconColor: 'text-amber-300',
    heading: 'text-amber-100',
  },
  success: {
    Icon: CheckCircle2,
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    iconColor: 'text-emerald-300',
    heading: 'text-emerald-100',
  },
  tip: {
    Icon: Lightbulb,
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    iconColor: 'text-violet-300',
    heading: 'text-violet-100',
  },
} as const;

const CalloutBlockRender: React.FC<{ block: CalloutContentBlock }> = ({ block }) => {
  const variant = CALLOUT_VARIANTS[block.variant] ?? CALLOUT_VARIANTS.info;
  const Icon = variant.Icon;
  return (
    <div className={`flex gap-4 rounded-3xl border ${variant.bg} ${variant.border} p-6 md:p-8`}>
      <div className={`shrink-0 ${variant.iconColor}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        {block.heading && (
          <h3 className={`text-lg md:text-xl font-black mb-2 ${variant.heading}`}>
            {block.heading}
          </h3>
        )}
        <SanitizedHtml
          className={`${PROSE_BASE} prose-p:my-2 first:prose-p:mt-0 last:prose-p:mb-0`}
          html={block.text}
        />
      </div>
    </div>
  );
};

const QuoteBlockRender: React.FC<{ block: QuoteContentBlock }> = ({ block }) => {
  const emphasized = block.variant === 'emphasized';
  return (
    <figure
      className={`relative rounded-3xl px-8 py-10 md:px-12 md:py-12 ${
        emphasized
          ? 'bg-gradient-to-br from-emerald-900/30 via-slate-900/40 to-slate-900/40 border border-emerald-700/30'
          : 'bg-slate-900/30 border border-slate-800'
      }`}
    >
      <QuoteIcon
        className={`absolute top-6 left-6 w-8 h-8 ${
          emphasized ? 'text-emerald-400/40' : 'text-slate-700'
        }`}
        aria-hidden="true"
      />
      <blockquote
        className={`relative pl-10 ${
          emphasized ? 'text-emerald-50 [html.light_&]:text-emerald-200' : 'text-slate-200'
        } text-lg md:text-xl font-medium italic leading-relaxed`}
      >
        {block.text}
      </blockquote>
      {block.attribution && (
        <figcaption
          className={`relative pl-10 mt-4 text-sm font-black uppercase tracking-widest ${
            emphasized ? 'text-emerald-300' : 'text-fg-muted'
          }`}
        >
          — {block.attribution}
        </figcaption>
      )}
    </figure>
  );
};

const DividerBlockRender: React.FC<{ block: DividerContentBlock }> = ({ block }) => {
  const style = block.style ?? 'line';
  if (style === 'space') {
    return <div aria-hidden="true" className="h-16" />;
  }
  if (style === 'dots') {
    return (
      <div aria-hidden="true" className="flex items-center justify-center gap-3 my-8">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />
        <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />
        <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />
      </div>
    );
  }
  return (
    <hr
      aria-hidden="true"
      className="my-8 h-px border-0 bg-gradient-to-r from-transparent via-slate-700 to-transparent"
    />
  );
};

const IMAGE_WIDTH: Record<string, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  full: 'max-w-none',
};

const ImageBlockRender: React.FC<{ block: ImageContentBlock }> = ({ block }) => {
  if (!block.src) return null;
  const alignment = block.alignment ?? 'center';
  const width = block.width ?? 'lg';
  const widthClass = alignment === 'full' ? 'max-w-none' : IMAGE_WIDTH[width];
  return (
    <figure className={`${alignment === 'center' ? 'mx-auto' : ''} ${widthClass}`}>
      <img
        src={block.src}
        alt={block.alt ?? ''}
        className="w-full rounded-2xl object-cover border border-slate-800"
      />
      {block.caption && (
        <figcaption className="mt-3 text-xs text-fg-muted font-medium text-center">
          {block.caption}
        </figcaption>
      )}
    </figure>
  );
};

/** Dispatcher: renders a single block according to its `kind`. */
export const ContentBlock: React.FC<{ block: ArticleContentBlock }> = ({ block }) => {
  if (isTableBlock(block)) {
    return <TableBlockRender block={block} />;
  }
  if (isFAQBlock(block)) {
    return (
      <div className="mb-12">
        <FAQBlockRender block={block} />
      </div>
    );
  }
  if (isCalloutBlock(block)) {
    return (
      <div className="mb-12">
        <CalloutBlockRender block={block} />
      </div>
    );
  }
  if (isQuoteBlock(block)) {
    return (
      <div className="mb-12">
        <QuoteBlockRender block={block} />
      </div>
    );
  }
  if (isDividerBlock(block)) {
    return (
      <div className="mb-12">
        <DividerBlockRender block={block} />
      </div>
    );
  }
  if (isImageBlock(block)) {
    return (
      <div className="mb-12">
        <ImageBlockRender block={block} />
      </div>
    );
  }
  return (
    <div className="mb-12">
      <RichBlock block={block} />
    </div>
  );
};
